import { useState, useEffect } from 'react';
import { Download, X, Smartphone } from 'lucide-react';

const PWAInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Check if already installed
    const standalone = window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true;
    setIsStandalone(standalone);

    // Check if iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    setIsIOS(iOS);

    // Check if dismissed recently
    const dismissed = localStorage.getItem('pwa-prompt-dismissed');
    if (dismissed) {
      const dismissedTime = parseInt(dismissed, 10);
      // Show again after 7 days
      if (Date.now() - dismissedTime < 7 * 24 * 60 * 60 * 1000) {
        return;
      }
    }

    // Listen for beforeinstallprompt event (Chrome, Edge, etc.)
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // Show prompt after 30 seconds on the site
      setTimeout(() => {
        if (!standalone) setShowPrompt(true);
      }, 30000);
    };

    window.addEventListener('beforeinstallprompt', handler);

    // For iOS, show prompt after 30 seconds
    if (iOS && !standalone) {
      setTimeout(() => setShowPrompt(true), 30000);
    }

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setShowPrompt(false);
        setDeferredPrompt(null);
      }
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('pwa-prompt-dismissed', Date.now().toString());
  };

  // Don't show if already installed
  if (isStandalone || !showPrompt) return null;

  return (
    <div 
      className="card-floating fixed bottom-20 left-4 right-4 md:left-auto md:right-4 md:w-80 z-50 animate-slide-up" 
      style={{ padding: 16 }}
      data-testid="pwa-install-prompt"
    >
      <button
        onClick={handleDismiss}
        style={{
          position: 'absolute',
          top: 12,
          right: 12,
          padding: 4,
          background: 'transparent',
          border: 'none',
          color: 'var(--text-muted)',
          cursor: 'pointer'
        }}
      >
        <X className="w-5 h-5" />
      </button>
      
      <div className="flex items-start gap-3">
        <div style={{ padding: 12, background: 'rgba(198,170,88,0.15)', borderRadius: 12 }}>
          <Smartphone className="w-6 h-6" style={{ color: 'var(--gold)' }} />
        </div>
        <div className="flex-1 pr-6">
          <h3 style={{ fontWeight: 600, color: 'var(--text)' }}>Install SFarm Monitor</h3>
          <p style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4 }}>
            {isIOS 
              ? "Tap the share button and 'Add to Home Screen' for quick access"
              : "Add to your home screen for quick access anytime"
            }
          </p>
        </div>
      </div>
      
      {!isIOS && deferredPrompt && (
        <button
          onClick={handleInstall}
          className="btn-depth-primary w-full mt-4 flex items-center justify-center gap-2"
          style={{ padding: '12px 16px' }}
          data-testid="pwa-install-button"
        >
          <Download className="w-5 h-5" />
          Install App
        </button>
      )}
      
      {isIOS && (
        <div style={{ 
          marginTop: 16, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          gap: 8, 
          background: 'rgba(255,255,255,0.05)', 
          padding: '12px 16px', 
          borderRadius: 12, 
          fontSize: 13,
          color: 'var(--text-secondary)'
        }}>
          <span>Tap</span>
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path d="M10 2a1 1 0 011 1v5.586l2.293-2.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L9 8.586V3a1 1 0 011-1z"/>
            <rect x="3" y="12" width="14" height="6" rx="1"/>
          </svg>
          <span>then "Add to Home Screen"</span>
        </div>
      )}
    </div>
  );
};

export default PWAInstallPrompt;
